import type { APIDesignerStore } from '..';
import type { WritableDraft } from 'immer/dist/internal.js';
import type { OpenAPIV3 } from 'openapi-types';

import type { PartialSchema } from '@ui/APIDesigner/TypeMenu';

const schemaPropertyMappingPattern = /0(-\d+)+/g;

export type UpdateSchemaAttrObject =
  | { attribute: 'default'; value: string }
  | { attribute: 'description'; value: string }
  | { attribute: 'name'; value: string }
  | { attribute: 'required'; value: boolean }
  | { attribute: 'type'; value: PartialSchema };

type TraverseAction =
  | {
      action: 'add' | 'remove';
    }
  | (UpdateSchemaAttrObject & { action: 'update' });

/**
 * Traverses the schema object (provided via the `state`) and modifies it based on the provided `id` and `action`.
 *
 * @param id The id of the property to remove. This is a string that represents the path to the property in the schema.
 * The first element is always 0, which represents the top-level object.
 * If the id is "0", it means the entire request body should be removed.
 * If the id is "0-1-2", it means the property should be removed from the third property of the second property of the root schema object.
 * @param state The current state of the APIDesigner store.
 * @param action The action to perform on the schema. This can be one of the following:
 * - `add`: Adds a new property to the schema.
 * - `remove`: Removes a property from the schema.
 * - `update`: Updates an attribute of the schema to a provided value.
 */
export function traverseAndModifySchema(
  id: string,
  state: WritableDraft<APIDesignerStore>,
  action: TraverseAction,
): void {
  if (!state.apiObject) throw new Error('apiObject is required');
  if (!state.apiObject.schema) throw new Error('schema is required');
  if (!state.apiObject.schema.paths) throw new Error('paths is required');
  if (!state.apiObject.path) throw new Error('path is required');
  if (!state.apiObject.method) throw new Error('method is required');

  const currentPath = state.apiObject.path;
  const currentMethod = state.apiObject.method;

  if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
    throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
  }

  const requestBody = state.apiObject.schema.paths[currentPath][currentMethod]?.requestBody;

  if (!requestBody) {
    // no request body found, so we can't do anything
    return;
  }

  if ('$ref' in requestBody) {
    throw new Error('Cannot add a sub-property to a reference request body');
  }

  // by the time we get here, we've done enough type checking to know
  // that currentSchema is a SchemaObject, so we can safely cast it
  const currentSchema = requestBody.content?.['application/json']?.schema;

  if (!currentSchema) {
    // no JSON request body schema found, so we can't do anything
    return;
  }

  if ('$ref' in currentSchema) {
    throw new Error('Cannot add a sub-property to a reference request body');
  }

  if (!id.match(schemaPropertyMappingPattern)) {
    return;
  }

  // if the id is not 0, this means the id contains a map to a sub-property
  const propertyIndexMap = id.split('-').map(Number);
  // we strip away the first element since it's always 0, which represents the top-level object.
  propertyIndexMap.splice(0, 1);

  /**
   * Keeps track of the current nested level of the object being traversed.
   */
  let currentNestedLevel = 0;
  /**
   * Represents the total number of nested levels that were defined in `propertyIndexMap`.
   */
  const totalNestedLevels = propertyIndexMap.length;
  /**
   * The object to traverse. This is the current schema object or the request body schema.
   *
   * As we traverse the object and its sub-properties, we update this variable to point to the current object.
   */
  let objectToTraverse: WritableDraft<OpenAPIV3.SchemaObject> = currentSchema || {};

  /**
   * Traverse the object to find the property to add.
   * `propertyIndexMap` is an array of indices that represent the path to the property.
   *
   * For example, if the propertyIndexMap is `[1, 2]`, it means the the object to add a property to is
   * the 3rd property of the 2nd property of the root schema object.
   */
  propertyIndexMap.some((propertyIndex, i) => {
    const currentPropertyName = Object.keys(objectToTraverse.properties || {})[propertyIndex];

    if (typeof currentPropertyName !== 'string') {
      // handling in case the property index map contains invalid data
      return false;
    }

    const schemaToUpdate = objectToTraverse.properties?.[currentPropertyName];
    // This if-statement checks that we've reached the last nested level defined in `propertyIndexMap`
    // and that the current nested level is the same as the one we're on.
    // If so, we've found the object to make changes to to.
    if (i >= totalNestedLevels - 1 && i === currentNestedLevel) {
      switch (action.action) {
        // we're adding a new property to the schema
        case 'add':
          if (schemaToUpdate && 'properties' in schemaToUpdate) {
            schemaToUpdate.properties = {
              ...schemaToUpdate.properties,
              '': { type: 'string' },
            };
          } else if (schemaToUpdate && 'items' in schemaToUpdate && 'properties' in schemaToUpdate.items) {
            // if the schemaToUpdate is an array of objects, we need to add the new property to the items object
            schemaToUpdate.items.properties = {
              ...schemaToUpdate.items.properties,
              '': { type: 'string' },
            };
          }
          break;
        // we're removing a property from the schema
        case 'remove':
          if (objectToTraverse.properties) {
            delete objectToTraverse.properties[currentPropertyName];
          }

          // In addition to removing the property, we also need to remove it from the required array
          // (if it's in there).
          if (objectToTraverse.required) {
            const requiredIndex = objectToTraverse.required.indexOf(currentPropertyName);
            if (requiredIndex !== -1) {
              objectToTraverse.required.splice(requiredIndex, 1);
            }
          }
          break;
        // we're updating an attribute of the schema
        case 'update':
          // eslint-disable-next-line no-case-declarations
          const { attribute, value } = action;
          // we're updating a attribute of the schema
          if (
            // these two conditions are to ensure that we don't try to update a reference object
            // (which is unlikely, but this is a nice type guard)
            schemaToUpdate &&
            !('$ref' in schemaToUpdate)
          ) {
            switch (attribute) {
              case 'default':
                schemaToUpdate.default = value;
                break;
              case 'description':
                schemaToUpdate.description = value;
                break;
              case 'name':
                if (objectToTraverse.properties) {
                  // This is a bit cumbersome, but rather than adding a new property to the object
                  // and deleting the old one, we recreate the entire object one key at a time,
                  // replacing the old property name with the new one. This is to ensure that
                  // the order of the properties is preserved.
                  objectToTraverse.properties = Object.keys(objectToTraverse.properties).reduce<
                    typeof objectToTraverse.properties
                  >((prev, curr) => {
                    if (curr === currentPropertyName) {
                      prev[value] = objectToTraverse.properties![curr];
                    } else {
                      prev[curr] = objectToTraverse.properties![curr];
                    }
                    return prev;
                  }, {});

                  // if the surrounding object has an array of required fields, we need to
                  // make sure it reflects the new property name
                  if (objectToTraverse.required) {
                    // check if the current property name is in the required array
                    // and if it is, we need to update it to the new property name
                    const requiredIndex = objectToTraverse.required.indexOf(currentPropertyName);
                    if (requiredIndex !== -1) {
                      objectToTraverse.required[requiredIndex] = value;
                    }

                    // remove any duplicates of the old and new property names, if they exist
                    objectToTraverse.required = objectToTraverse.required.filter(
                      (item, index) => index === requiredIndex || item !== currentPropertyName || item !== value,
                    );
                  }
                }
                break;
              case 'required':
                // if the property is required, we need to update the required array.
                // first, we check if the required array exists
                if (objectToTraverse.required) {
                  // we need to remove the property from the required array if it exists
                  if (!value) {
                    objectToTraverse.required = objectToTraverse.required.filter(prop => prop !== currentPropertyName);
                    if (!objectToTraverse.required.length) {
                      // if the required array is empty, we should delete it
                      delete objectToTraverse.required;
                    }
                  } else if (value) {
                    const requiredIndex = objectToTraverse.required.indexOf(currentPropertyName);
                    // the required array doesn't contain the property name and we need to add it
                    if (requiredIndex === -1) {
                      objectToTraverse.required.push(currentPropertyName);
                    }
                  }
                } else if (value) {
                  // if the required array doesn't exist, we need to create it
                  objectToTraverse.required = [currentPropertyName];
                }
                break;
              case 'type':
                schemaToUpdate.type = value.type;

                // set a format if it exists, otherwise delete it
                if (value.format) {
                  schemaToUpdate.format = value.format;
                } else {
                  delete schemaToUpdate.format;
                }

                // if the type is an object and there are no properties, create an empty properties object
                // otherwise, remove the `properties` and `required` fields
                if (value.type === 'object') {
                  if (!schemaToUpdate.properties) schemaToUpdate.properties = {};
                } else {
                  delete schemaToUpdate.properties;
                  delete schemaToUpdate.required;
                }

                // if the type is an array, set the items field
                // otherwise, remove the items field
                if (value.type === 'array' && value.items) {
                  (schemaToUpdate as OpenAPIV3.ArraySchemaObject).items = value.items;
                } else {
                  // @ts-expect-error we want to delete this property whether or not it exists
                  delete schemaToUpdate.items;
                }
                break;
              default:
            }
          }
          break;
        default:
      }

      // We've successfully updated the schema, so we can return true
      // to stop traversing `propertyIndexMap`.
      return true;
    }

    // If we've reached this point in the logic, it means we haven't made any changes to the schema nor have we
    // reached the last nested level defined in `propertyIndexMap`.
    // If the current property is an object, we need to update `objectToTraverse` to point to the current property
    // so we do the traversal again.
    if (schemaToUpdate && 'properties' in schemaToUpdate && schemaToUpdate.properties) {
      currentNestedLevel += 1;
      objectToTraverse = schemaToUpdate;
    } else if (
      schemaToUpdate &&
      'items' in schemaToUpdate &&
      'type' in schemaToUpdate.items &&
      schemaToUpdate.items.type === 'object'
    ) {
      // if the schemaToUpdate is an array of objects, we need to recurse into the items object
      currentNestedLevel += 1;
      objectToTraverse = schemaToUpdate.items;
    }

    return false;
  });
}
